/**
 * Which music plays: a file from `/music` when there is one for the moment, otherwise the
 * synthesised score. While a file plays the synth's music bus is turned right down - its
 * stingers and sound effects are untouched - and it comes back the moment no file answers.
 */

import type { GameAudio } from '../audio';
import { FileMusic } from '../audio/fileMusic';
import {
  BRIEFING_TRACKS, FAIL_TRACKS, MEMORIAL_TRACKS, missionTrackNames, SUCCESS_TRACKS, TITLE_TRACKS,
} from '../audio/trackNames';
import type { MissionDef } from '../sim/types';
import type { Settings } from '../ui/screens';

/** Sliders are perceptual, like the synth's own (`sliderGain`). */
const gain = (slider: number): number => slider * slider;

export class MusicDirector {
  private readonly files = new FileMusic();
  private settings: Settings | null = null;
  /** How the last mission went: chooses the piece for the after-action report. */
  private succeeded = false;

  constructor(private readonly audio: GameAudio) {
    Object.assign(window, {
      __music: { tracks: () => this.files.names(), playing: () => this.files.playing, position: () => this.files.position },
    });
  }

  applySettings(s: Settings): void {
    this.settings = s;
    this.files.setVolume(gain(s.masterVolume) * gain(s.musicVolume));
    this.audio.applySettings({
      master: s.masterVolume, sfx: s.sfxVolume,
      // The two scores never play over each other.
      music: this.files.playing !== null ? 0 : s.musicVolume,
    });
  }

  /** Browsers hold audio until the first key or click. */
  unlock(): void {
    this.audio.resume();
    this.files.unlock();
  }

  title(): void {
    if (this.switchTo(TITLE_TRACKS)) this.audio.stopMusic();
    else this.audio.playTitleTheme();
  }

  /** The briefing has its own track if there is one; otherwise whatever was playing carries on. */
  briefing(): void {
    if (this.files.playing !== null || this.hasAny(BRIEFING_TRACKS)) this.switchTo(this.hasAny(BRIEFING_TRACKS) ? BRIEFING_TRACKS : TITLE_TRACKS);
  }

  /** Call when a mission ends, before the menus come back: success is home with the job done. */
  setOutcome(succeeded: boolean): void {
    this.succeeded = succeeded;
  }

  /**
   * After a mission: the success or failure piece, played once, on the report; the memorial
   * track, looping, on the memorial page. With no file for it: silence, as before.
   */
  afterMission(memorial: boolean): void {
    this.audio.stopMusic();
    const ok = memorial
      ? this.files.play(MEMORIAL_TRACKS)
      : this.files.play(this.succeeded ? SUCCESS_TRACKS : FAIL_TRACKS, true);
    if (!ok) this.files.stop();
    this.resync();
  }

  /** Call before `audio.beginMission`: with a file for this ground and hour, the synth score stays silent. */
  mission(def: MissionDef): void {
    this.switchTo(missionTrackNames(def.biome, def.timeOfDay));
  }

  private hasAny(names: readonly string[]): boolean {
    return names.some((n) => this.files.names().includes(n));
  }

  private switchTo(names: readonly string[]): boolean {
    const ok = this.files.play(names);
    this.resync();
    return ok;
  }

  private resync(): void {
    if (this.settings) this.applySettings(this.settings);
  }
}
